import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { DEFAULT_FILTER } from '../user';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import {
  ClubProgrammeCreatePayload,
  ClubProgrammeUpdatePayload,
  ClubProgrammeAddClubPayload,
  ClubProgrammeUpdateClubPayload,
  ClubProgrammeBatchActivateResult,
  ClubProgrammeBookingsResponse,
  ClubProgrammeAllocationOutcome,
  ClubProgrammeActivationPreviewResponse,
  ClubProgrammeBatchActivateClubsResult,
  ClubProgrammeActionResult,
  ClubProgrammeBulkSelectClubPayload,
  ClubProgrammeBulkSelectClubResult,
  normalizeBatchAddPayload,
  normalizeBatchRemovePayload
} from 'Src/helpers/service/admin/clubProgrammes';

export function getClubProgrammes(user: AppUser, filter = DEFAULT_FILTER): Promise<AfterSchoolClubProgramme[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes`, config).then(response => {
    return response.data;
  });
}

export function getAllClubProgrammes(user: AppUser, where?): Promise<AfterSchoolClubProgramme[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes`, config).then(response => {
    return response.data;
  });
}

export function getClubProgrammesCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/count`, config)
    .then(response => {
      return response.data;
    });
}

export function createClubProgramme(
  user: AppUser,
  data: ClubProgrammeCreatePayload
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes`, data, config).then(response => {
    return response.data;
  });
}

export function getClubProgramme(user: AppUser, programmeId: string): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}`, config)
    .then(response => {
      return response.data;
    });
}

export function updateClubProgramme(
  user: AppUser,
  data: ClubProgrammeUpdatePayload,
  programmeId: string
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}`, data, config)
    .then(response => {
      const payload = response.data;

      if (payload && (payload.error || payload.code)) {
        const err: any = new Error(payload.error || 'Failed to update programme.');
        err.response = { data: payload };
        throw err;
      }

      return payload;
    });
}

export function batchActivateClubProgrammes(
  user: AppUser,
  programmeIdList: string[]
): Promise<ClubProgrammeBatchActivateResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  if (!Array.isArray(programmeIdList) || programmeIdList.length === 0) {
    return Promise.reject(new Error('programmeIdList is required'));
  }

  const payload = { programmeIdList: programmeIdList.map(x => String(x)) };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/batch-activate`, payload, {
      headers: {
        usid: key,
        'Content-Type': 'application/json'
      }
    })
    .then(r => r.data);
}

export function deleteClubProgramme(user: AppUser, programmeId: string): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}`, config)
    .then(response => {
      return response.data;
    });
}

export function addClubToProgramme(
  user: AppUser,
  programmeId: string,
  data: ClubProgrammeAddClubPayload
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  const payload: ClubProgrammeAddClubPayload = {
    clubId: data.clubId
  };

  if (typeof (data as any).isEnabled !== 'undefined') {
    payload.isEnabled = !!(data as any).isEnabled;
  }

  if (typeof (data as any).fieldsOverride !== 'undefined') {
    payload.fieldsOverride = (data as any).fieldsOverride;
  }

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs`, payload, config)
    .then(response => {
      return response.data;
    });
}

export function updateProgrammeClub(
  user: AppUser,
  programmeId: string,
  programmeClubId: string,
  data: ClubProgrammeUpdateClubPayload
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs/${programmeClubId}`,
      data,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function deleteProgrammeClub(
  user: AppUser,
  programmeId: string,
  programmeClubId: string
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs/${programmeClubId}`,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function addProgrammeClubsBatch(
  user: AppUser,
  programmeId: string,
  data: any
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const payload = normalizeBatchAddPayload(data);

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs/batch-add`, payload, {
      headers: {
        usid: key,
        'Content-Type': 'application/json'
      }
    })
    .then(r => r.data);
}

export function removeProgrammeClubsBatch(
  user: AppUser,
  programmeId: string,
  data: any
): Promise<AfterSchoolClubProgramme> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const payload = normalizeBatchRemovePayload(data);

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs/batch-remove`, payload, {
      headers: {
        usid: key,
        'Content-Type': 'application/json'
      }
    })
    .then(r => r.data);
}

export function getClubProgrammeBookings(
  user: AppUser,
  programmeId: string,
  opts?: { statuses?: string[]; includeDrafts?: boolean }
): Promise<ClubProgrammeBookingsResponse> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const statuses = opts?.statuses && opts.statuses.length ? opts.statuses.join(',') : 'ACCEPTED';

  const includeDrafts = opts?.includeDrafts ? '1' : '0';

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings`, {
      headers: { usid: key },
      params: { statuses, includeDrafts }
    })
    .then(r => r.data);
}

export function selectClubProgrammeBooking(
  user: AppUser,
  programmeId: string,
  messageId: string,
  allocationOutcome: ClubProgrammeAllocationOutcome
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/${messageId}/select`,
      { allocationOutcome },
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function unselectClubProgrammeBooking(
  user: AppUser,
  programmeId: string,
  messageId: string
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/${messageId}/unselect`,
      {},
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function bulkSelectClubProgrammeBookingsForClub(
  user: AppUser,
  programmeId: string,
  data: ClubProgrammeBulkSelectClubPayload
): Promise<ClubProgrammeBulkSelectClubResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/bulk-select-club`,
      {
        clubId: data.clubId,
        mode: data.mode,
        restToWaitingList: !!data.restToWaitingList
      },
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function bulkSelectClubProgrammeBookingsForDay(
  user: AppUser,
  programmeId: string,
  data: {
    selectionDayKey: string;
    mode: 'ALL' | 'FIRST_N';
    restToWaitingList?: boolean;
  }
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/bulk-select-day`,
      data,
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function bulkSelectClubProgrammeBookingsForProgramme(
  user: AppUser,
  programmeId: string,
  data: {
    mode: 'ALL' | 'FIRST_N';
    restToWaitingList?: boolean;
  }
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/bulk-select-programme`,
      data,
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function confirmClubProgrammeBookingsForClub(
  user: AppUser,
  programmeId: string,
  clubId: string,
  resolutions?: Array<{ permissionId: string; keepMessageId: string }>
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/confirm-club`,
      {
        clubId,
        resolutions: Array.isArray(resolutions) ? resolutions : []
      },
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function confirmClubProgrammeBookingsForDay(
  user: AppUser,
  programmeId: string,
  selectionDayKey: string,
  resolutions: Array<{ permissionId: string; keepMessageId: string }> = []
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/confirm-day`,
      {
        selectionDayKey,
        resolutions
      },
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function confirmClubProgrammeBookingsForProgramme(
  user: AppUser,
  programmeId: string,
  resolutions: Array<{ dayKey: string; permissionId: string; keepMessageId: string }> = []
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/confirm-programme`,
      {
        resolutions
      },
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function removeClubProgrammeBooking(
  user: AppUser,
  programmeId: string,
  messageId: string
): Promise<ClubProgrammeActionResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/bookings/${messageId}/remove`,
      {},
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}

export function getClubProgrammeActivationPreview(
  user: AppUser,
  programmeId: string
): Promise<ClubProgrammeActivationPreviewResponse> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs-batch-activate-preview`, {
      headers: { usid: key }
    })
    .then(r => r.data);
}

export function batchActivateClubProgrammeClubs(
  user: AppUser,
  programmeId: string,
  data?: { timeZoneName?: string }
): Promise<ClubProgrammeBatchActivateClubsResult> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const payload = data || {};

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/clubs/programmes/${programmeId}/clubs-batch-activate`,
      payload,
      {
        headers: {
          usid: key,
          'Content-Type': 'application/json'
        }
      }
    )
    .then(r => r.data);
}
